import React, { useState } from 'react';

import type { StoreMarkerModel } from 'models/sitecore';
import { getFormattedStockAmount } from 'utils/business-logic/stock';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import InfoWindowContent from './InfoWindowContent';
import MapBase from './MapBase';
import MarkerWithInfoWindow from './MarkerWithInfoWindow';

export interface Props {
	className?: string;

	/** className, can be used to control the height of the map. */
	mapClassName?: string;

	/** The stores nearby based on browser location */
	nearbyStores?: StoreMarkerModel[];

	/** List of stores to display as markers */
	stores: StoreMarkerModel[];

	userPosition?: {
		lat: number;
		long: number;
	};
}

export default function StoreMap({
	className,
	mapClassName,
	nearbyStores,
	stores,
	userPosition,
}: Props) {
	const { t } = useI18n();

	const [selectedStoreId, setSelectedStoreId] = useState<string | undefined>();

	return (
		<MapBase
			markers={is.arrayWithLength(nearbyStores) ? nearbyStores : stores}
			mapClassName={mapClassName}
			className={className}
		>
			{userPosition && (
				<MarkerWithInfoWindow
					position={{
						lat: userPosition.lat,
						lng: userPosition.long,
					}}
					title={t('stores_map_your_position_text', {
						lat: userPosition.lat,
						long: userPosition.long,
					})}
					onClose={() => setSelectedStoreId(undefined)}
					onSelectClick={() => setSelectedStoreId('userPosition')}
					isSelected={selectedStoreId === 'userPosition'}
					iconSrc="/assets/images/icons/icon-pin-black.svg"
					content={
						<span>
							{t('stores_map_your_position_text', {
								lat: userPosition.lat,
								long: userPosition.long,
							})}
						</span>
					}
				/>
			)}
			{stores.map((store) => (
				<MarkerWithInfoWindow
					key={store.id}
					isSelected={selectedStoreId === store.id}
					onSelectClick={() => setSelectedStoreId(store.id)}
					onClose={() => setSelectedStoreId(undefined)}
					iconSrc="/assets/images/icons/icon-pin-jula.svg"
					position={{
						lat: store.latitude,
						lng: store.longitude,
					}}
					title={store.name}
					content={
						<InfoWindowContent
							isOpen={store.isOpen}
							linkText={t('stores_map_more_info_text')}
							linkUrl={store.url}
							openHours={store.openHours}
							storeArea={store.storeArea}
							address={store.address}
							onSelectClick={store.onSelectClick}
							isSelected={store.isSelected}
							selectButtonText={t('stores_pick_as_store_button')}
							isSelectedMessage={t('stores_store_picked_as_store_text', {
								storeName: store.name,
							})}
							inStock={store.inStock}
							availableStockLevelText={t('product_details_in_stock_label', {
								amount: getFormattedStockAmount(store.availableStockLevel),
							})}
						/>
					}
				/>
			))}
		</MapBase>
	);
}
StoreMap.displayName = 'StoreMap';
